export const MINIMUM_PASSWORD_LENGTH = 12
export const MAX_PASSWORD_LENGTH = 64

export type PasswordRequirement = {
  key: string
  label: string
  test: (password: string) => boolean
}

export const PASSWORD_RULES: PasswordRequirement[] = [
  {
    key: 'minLength',
    label: `At least ${MINIMUM_PASSWORD_LENGTH} characters`,
    test: (p) => p.length >= MINIMUM_PASSWORD_LENGTH
  },
  {
    key: 'maxLength',
    label: `No more than ${MAX_PASSWORD_LENGTH} characters`,
    test: (p) => p.length <= MAX_PASSWORD_LENGTH
  },
  {
    key: 'uppercase',
    label: 'At least one uppercase letter',
    test: (p) => /[A-Z]/.test(p)
  },
  {
    key: 'lowercase',
    label: 'At least one lowercase letter',
    test: (p) => /[a-z]/.test(p)
  },
  {
    key: 'digit',
    label: 'At least one digit',
    test: (p) => /[0-9]/.test(p)
  },
  {
    key: 'special',
    label: 'At least one special character',
    test: (p) => /[^A-Za-z0-9]/.test(p)
  }
]

export type PasswordRequirements = Record<string, boolean>

export function checkPasswordRequirements(
  password: string
): PasswordRequirements {
  return Object.fromEntries(
    PASSWORD_RULES.map((rule) => [rule.key, rule.test(password)])
  )
}

export function isPasswordValid(password: string): boolean {
  return PASSWORD_RULES.every((rule) => rule.test(password))
}

export function getPasswordStrength(password: string): {
  percent: number
  label: string
  color: 'error' | 'warning' | 'info' | 'success'
} {
  const met = PASSWORD_RULES.filter((rule) => rule.test(password)).length
  const percent = Math.round((met / PASSWORD_RULES.length) * 100)

  if (percent <= 33) return { percent, label: 'Weak', color: 'error' }
  if (percent <= 50) return { percent, label: 'Fair', color: 'warning' }
  if (percent <= 83) return { percent, label: 'Good', color: 'info' }
  return { percent, label: 'Strong', color: 'success' }
}
